import React, {useEffect, useState} from "react";
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    Row,
    Select,
    Space,
    Typography
} from "antd";
import {ComponentPropsFromRoute} from "../../../../routers/routers";
import {useTranslate} from "../../../../hooks/translate.hook";
import {loggerService} from "../../../../services/logger/logger.service";
import {ChangeLogModal} from "../../change-log/modal/change-log.modal";
import {EntityLogModal} from "../../entity/modal/entity.modal";
import {useForm} from "antd/es/form/Form";
import {useRequiredStore} from "../../../../utils/store";
import {RootStoreContext} from "../../../../stores/root/root.store";

type LoggerFormValues = {
    name: string;
    id: string;
};

export const LoggerPage: React.FC<ComponentPropsFromRoute> = ({name}) => {
    const t = useTranslate();
    const {authStore} = useRequiredStore(RootStoreContext);

    const [entities, setEntities] = useState<string[]>([]);
    const [entitiesLoading, setEntitiesLoading] = useState<boolean>(false);

    const [changeLogModalShown, setChangeLogModalShown] = useState<boolean>(false);
    const [entityLogModalShown, setEntityLogModalShown] = useState<boolean>(false);

    const [entityName, setEntityName] = useState<string>("");
    const [entityId, setEntityId] = useState<string>("");

    const [form] = useForm<LoggerFormValues>();

    useEffect(() => {
        setEntitiesLoading(true);

        loggerService
            .getEntitiesList()
            .then(setEntities)
            .finally(() => setEntitiesLoading(false));
    }, []);

    const openLogModal = async (type: "logs" | "changeLog") => {
        const values = await form.validateFields();

        setEntityId(values.id);
        setEntityName(values.name);

        if (type === "logs") {
            setEntityLogModalShown(true);
        }

        if (type === "changeLog") {
            setChangeLogModalShown(true);
        }
    };

    return (
        <>
            <Space
                direction="vertical"
                size={24}
                style={{
                    width: "100%",
                    maxWidth: 900
                }}
            >
                <Typography.Title level={3} style={{marginBottom: 4}}>
                    {t("LOGGER.TITLE")}
                </Typography.Title>

                <Row gutter={[24, 24]}>

                    <Col xs={24} md={14}>
                        <Card
                            title="Search logs by entity"
                            bordered={false}
                            style={{height: "100%"}}
                        >
                            <Form
                                form={form}
                                layout="vertical"
                                requiredMark={false}
                            >
                                <Form.Item
                                    label="Entity"
                                    name="name"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE")
                                        }
                                    ]}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        loading={entitiesLoading}
                                        placeholder="Entity"
                                        optionFilterProp="label"
                                        options={entities.map(entity => ({
                                            value: entity,
                                            label: entity
                                        }))}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Entity ID"
                                    name="id"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE")
                                        }
                                    ]}
                                >
                                    <Input placeholder="Entity ID" />
                                </Form.Item>

                                <Space
                                    size={12}
                                    style={{
                                        width: "100%",
                                        justifyContent: "flex-end"
                                    }}
                                    wrap
                                >
                                    <Button onClick={() => openLogModal("logs")}>
                                        Show logs
                                    </Button>

                                    <Button
                                        type="primary"
                                        onClick={() => openLogModal("changeLog")}
                                    >
                                        Show change log
                                    </Button>
                                </Space>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} md={10}>
                        <Card
                            title="Victoria Logs"
                            bordered={false}
                            style={{height: "100%"}}
                        >
                            <Space direction="vertical" size={16} style={{width: "100%"}}>
                                <Typography.Text type="secondary">
                                    Go to external logger Victoria Logs.
                                </Typography.Text>

                                <Button
                                    type="primary"
                                    href={"http://" + authStore.defaultServer + ":9429/select/vmui"}
                                    target="_blank"
                                    rel="noreferrer"
                                    block
                                >
                                    Go to Victoria Logs
                                </Button>
                            </Space>
                        </Card>
                    </Col>

                </Row>
            </Space>

            {changeLogModalShown && (
                <ChangeLogModal
                    onClose={() => setChangeLogModalShown(false)}
                    entities={[
                        {
                            id: entityId,
                            name: entityName
                        }
                    ]}
                />
            )}

            {entityLogModalShown && (
                <EntityLogModal
                    onClose={() => setEntityLogModalShown(false)}
                    entityId={entityId}
                    entityName={entityName}
                />
            )}
        </>
    );
};